'use client'

import { useEffect, useState, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import Image from 'next/image'
import { X, ArrowUpRight } from 'lucide-react'
import { Link } from '@/i18n/routing'
import { useTranslations, useLocale } from 'next-intl'
import { inter } from '@/lib/fonts'
import LanguageSelector from './HeaderLanguageSelector'
import useLanguageSwitcher from '../widgets/settings/useLanguageSwitcher'
import '../../sass/landing/LandingHeader.scss'

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"]), input, select, textarea'

const LandingHeader = () => {
  const t = useTranslations('LandingHeader')
  const tFooter = useTranslations('LandingFooter')
  const locale = useLocale()
  const { changeLanguage } = useLanguageSwitcher()

  const homeLinks = [
    { label: t('links.digitalMenu'), anchor: '#features' },
    { label: t('links.serviceReservations'), anchor: '#features-bookings' },
    { label: t('links.serviceDelivery'), anchor: '#features-delivery' },
    { label: t('links.pricing'), anchor: '#pricing' },
    { label: t('links.faq'), anchor: '#faq' }
  ]

  const [showMenu, setShowMenu] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [mounted, setMounted] = useState(false)

  const drawerRef = useRef<HTMLDivElement | null>(null)
  const closeButtonRef = useRef<HTMLButtonElement | null>(null)
  const triggerButtonRef = useRef<HTMLButtonElement | null>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const closeMenu = useCallback(() => setShowMenu(false), [])

  // Lock body scroll, manage ESC key and focus while the drawer is open.
  useEffect(() => {
    if (!showMenu) return

    previousFocusRef.current = document.activeElement as HTMLElement | null
    // Capture the trigger ref at effect-creation time so the cleanup
    // function focuses the same node, even if React swaps the ref later.
    const triggerButton = triggerButtonRef.current

    const previousOverflow = document.body.style.overflow
    const previousPaddingRight = document.body.style.paddingRight
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
    document.body.style.overflow = 'hidden'
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        closeMenu()
        return
      }
      if (event.key === 'Tab' && drawerRef.current) {
        const focusable = drawerRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
        if (focusable.length === 0) return
        const first = focusable[0]
        const last = focusable[focusable.length - 1]
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault()
          last.focus()
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault()
          first.focus()
        }
      }
    }
    document.addEventListener('keydown', onKeyDown)

    // Focus the close button once the drawer transition starts.
    const focusTimeout = window.setTimeout(() => {
      closeButtonRef.current?.focus()
    }, 80)

    return () => {
      window.clearTimeout(focusTimeout)
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = previousOverflow
      document.body.style.paddingRight = previousPaddingRight
      // Restore focus to the trigger (or the previously focused element) after close.
      const target = triggerButton ?? previousFocusRef.current
      target?.focus()
    }
  }, [showMenu, closeMenu])

  const handleNavClick = useCallback(() => {
    closeMenu()
  }, [closeMenu])

  return (
    <header className={`landing-header${scrolled ? ' landing-header--scrolled' : ''}`}>
      <div className='landing-header__inner'>
        <Link href='/' className='landing-header__logo' onClick={closeMenu}>
          <Image
            src='/img/barmanager-logo.svg'
            alt={t('altLogo')}
            height={200}
            width={600}
            priority
          />
        </Link>

        <nav className='landing-header__nav' aria-label='Primary'>
          <ul>
            {homeLinks.map((link) => (
              <li key={link.anchor}>
                <Link href={link.anchor}>{link.label}</Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className='landing-header__actions'>
          <LanguageSelector hidePc className='landing-header__lang' />
          <Link href='/login' className='landing-header__login'>
            {t('login')}
          </Link>
          <Link href='/login/signup' className='landing-header__cta'>
            {t('startFree')}
            <ArrowUpRight size={14} strokeWidth={2.5} />
          </Link>

          <button
            ref={triggerButtonRef}
            type='button'
            className={`landing-header__burger${showMenu ? ' is-active' : ''}`}
            aria-label={showMenu ? t('closeMenu') : t('openMenu')}
            aria-expanded={showMenu}
            aria-controls='landing-mobile-menu'
            onClick={() => setShowMenu((value) => !value)}
          >
            <span className='landing-header__burger-box' aria-hidden='true'>
              <span className='landing-header__burger-bar' />
              <span className='landing-header__burger-bar' />
              <span className='landing-header__burger-bar' />
            </span>
          </button>
        </div>
      </div>

      {mounted &&
        createPortal(
          <div
            id='landing-mobile-menu-portal'
            className={`landing-drawer-portal${showMenu ? ' is-open' : ''}`}
            aria-hidden={!showMenu}
          >
            <button
              type='button'
              className='landing-drawer-portal__backdrop'
              aria-label={t('closeMenu')}
              tabIndex={showMenu ? 0 : -1}
              onClick={closeMenu}
            />

            <aside
              ref={drawerRef}
              className={`${inter.className} landing-drawer`}
              role='dialog'
              aria-modal='true'
              aria-label={t('navLabel')}
              aria-hidden={!showMenu}
            >
              <div className='landing-drawer__top'>
                <Link
                  href='/'
                  className='landing-drawer__logo'
                  onClick={handleNavClick}
                  tabIndex={showMenu ? 0 : -1}
                >
                  <Image
                    src='/img/barmanager-logo.svg'
                    alt={t('altLogo')}
                    height={200}
                    width={600}
                  />
                </Link>
                <button
                  ref={closeButtonRef}
                  type='button'
                  className='landing-drawer__close'
                  aria-label={t('closeMenu')}
                  tabIndex={showMenu ? 0 : -1}
                  onClick={closeMenu}
                >
                  <X size={20} strokeWidth={2} />
                </button>
              </div>

              <nav className='landing-drawer__nav' aria-label='Mobile'>
                <span className='landing-drawer__label'>{t('navLabel')}</span>
                <ul>
                  {homeLinks.map((link) => (
                    <li key={link.anchor}>
                      <Link href={link.anchor} onClick={handleNavClick} tabIndex={showMenu ? 0 : -1}>
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>

              <div className='landing-drawer__lang'>
                <span className='landing-drawer__label'>{t('languageLabel')}</span>
                <div className='landing-drawer__lang-switch' role='group' aria-label={t('languageLabel')}>
                  <button
                    type='button'
                    className={locale === 'es' ? 'is-active' : ''}
                    tabIndex={showMenu ? 0 : -1}
                    onClick={() => changeLanguage('es')}
                  >
                    ES
                  </button>
                  <button
                    type='button'
                    className={locale === 'en' ? 'is-active' : ''}
                    tabIndex={showMenu ? 0 : -1}
                    onClick={() => changeLanguage('en')}
                  >
                    EN
                  </button>
                </div>
              </div>

              <div className='landing-drawer__footer'>
                <Link
                  href='/login'
                  className='landing-drawer__login'
                  onClick={handleNavClick}
                  tabIndex={showMenu ? 0 : -1}
                >
                  {t('login')}
                </Link>
                <Link
                  href='/login/signup'
                  className='landing-drawer__cta'
                  onClick={handleNavClick}
                  tabIndex={showMenu ? 0 : -1}
                >
                  {tFooter('cta')}
                  <ArrowUpRight size={16} strokeWidth={2.5} />
                </Link>
              </div>
            </aside>
          </div>,
          document.body
        )}
    </header>
  )
}

export default LandingHeader
